import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import defaultTexts from '../../../texts/texts'
import constants from '../../../config/constants'
import useWindowDimensions from '../../_common/WindowDimesnions'
import { shortenString } from '../../_common/_helpers/string'
import CustomCard from '../../_common/CustomCard'

const RegionServiceItem = (props) => {
  const { subscriptionName, service_name, region_name, services, identityServiceName, handleServiceEditDialogOpen } = props
  const theme = useTheme()
  const { width } = useWindowDimensions()

  let valueLimit = 22

  if (width < constants.lgBreakpoint) {
    valueLimit = 10
  } else if (width < constants.xlBreakpoint) {
    valueLimit = 15
  } else {
    valueLimit = 22
  }

  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [service, setService] = useState(null)
  const [configParams, setConfigParams] = useState(null)
  const [isConfigured, setIsConfigured] = useState(false)
  const [isIdentityProvider, setIsIdentityProvider] = useState(false)

  const serviceWrapperStyle = {
    mineight: '134px',
    marginBottom: '20px',
    boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.25)',
    padding: '10px',
    cursor: 'pointer',
    background: 'white',
  }

  const disabledServiceWrapperStyle = {
    height: '134px',
    marginBottom: '20px',
    boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.25)',
    padding: '10px',
    background: theme.palette.lightSilver,
    color: theme.palette.grayX11,
    fontSize: '14px',
    fontWeight: '500px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  }

  const notConfiguredServiceWrapperStyle = {
    height: '134px',
    marginBottom: '20px',
    boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.25)',
    padding: '10px',
    background: theme.palette.azureishWhite,
    color: theme.palette.oxfordBlue,
    fontSize: '14px',
    fontWeight: '500px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  }

  const serviceKeyStyle = {
    color: theme.palette.grayX11,
    fontSize: width >= constants.lgBreakpoint ? '14px' : '12px',
    fontWeight: '500',
    flex: 1,
  }

  const serviceValueStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: width >= constants.lgBreakpoint ? '14px' : '12px',
    fontWeight: '500',
    textAlign: 'right',
    flex: 1,
  }

  const getWrapperStyle = () => {
    if (!isConfigured && isIdentityProvider) {
      return disabledServiceWrapperStyle
    } else if (!service) {
      return notConfiguredServiceWrapperStyle
    } else {
      return serviceWrapperStyle
    }
  }

  const handleClickEdit = () => {
    if (service) {
      handleServiceEditDialogOpen({
        service,
        title: defaultTexts.edit + ' ' + service.title.value + ' ' + defaultTexts.service,
      })
    }    
  }

  useEffect(() => {
    setIsIdentityProvider(service_name === identityServiceName)
  }, [service_name, identityServiceName])

  useEffect(() => {
    const empty_service_object = services.find(item => item.service === service_name)

    const service_object = services.find(item =>
      item.service === service_name &&
      item.config_params &&
      (
        (item.config_params.is_identity_provider && item.config_params.default_region && item.config_params.default_region === region_name) ||
        (!item.config_params.is_identity_provider && item.config_params.region_name && item.config_params.region_name === region_name)
      )
    )

    setService(empty_service_object ? empty_service_object : null)
    setConfigParams((service_object && service_object.config_params) ? service_object.config_params : null)
  }, [service_name, region_name, services])

  useEffect(() => {
    let is_configured = false

    if (configParams) {
      if (configParams.is_identity_provider) {
        if (configParams.default_region && configParams.default_region === region_name) {
          is_configured = true
        }
      } else if (configParams.region_name && configParams.region_name === region_name) {
        is_configured = true
      }
    }

    setIsConfigured(is_configured)
  }, [region_name, configParams])
  
  return (
    <Box
      sx={getWrapperStyle()}
      onClick={ editPermission ? handleClickEdit : () => {} }
    >
      <CustomCard sx={{ height: '114px' }}>
        {
          service ?
          <>
            <Stack
              direction='row'
              alignItems='flex-start'
              justifyContent='space-between'
              spacing={1}
              sx={{ marginBottom: '8px' }}
            >
              <Box sx={serviceKeyStyle}>{defaultTexts.domain}:</Box>
              {
                configParams &&
                <>
                  {
                    shortenString(configParams.service_domain, valueLimit) === configParams.service_domain ?
                    <Box sx={serviceValueStyle}>{configParams.service_domain}</Box> :
                    <Tooltip title={configParams.service_domain}>
                      <Box sx={serviceValueStyle}>{shortenString(configParams.service_domain, valueLimit)}</Box>
                    </Tooltip>
                  }
                </>                
              }
            </Stack>

            <Stack
              direction='row'
              alignItems='flex-start'
              justifyContent='space-between'
              spacing={1}
              sx={{ marginBottom: '8px' }}
            >
              <Box sx={serviceKeyStyle}>{defaultTexts.apiVersion}:</Box>
              <Box sx={serviceValueStyle}>{configParams && configParams.api_version}</Box>
            </Stack>

            {
              configParams && isIdentityProvider ?
              <>
                <Stack
                  direction='row'
                  alignItems='flex-start'
                  justifyContent='space-between'
                  spacing={1}
                  sx={{ marginBottom: '8px' }}
                >
                  <Box sx={serviceKeyStyle}>{defaultTexts.isIdentityProvider}:</Box>
                  <Box sx={serviceValueStyle}>{defaultTexts.yes}</Box>
                </Stack>

                <Stack
                  direction='row'
                  alignItems='flex-start'
                  justifyContent='space-between'
                  spacing={1}
                  sx={{ marginBottom: '8px' }}
                >
                  <Box sx={serviceKeyStyle}>{defaultTexts.credentialsProvided}:</Box>
                  <Box sx={serviceValueStyle}>{defaultTexts.yes}</Box>
                </Stack>
              </> :
              <>
                {
                  subscriptionName !== 'openstackBackupAutomation' &&
                  <Stack
                    direction='row'
                    alignItems='flex-start'
                    justifyContent='space-between'
                    spacing={1}
                    sx={{ marginBottom: '8px' }}
                  >
                    <Box sx={serviceKeyStyle}>{defaultTexts.visibleToUis}:</Box>
                    <Box sx={serviceValueStyle}>
                      {
                        (configParams && configParams.visible_to_service && configParams.visible_to_service.includes('custom_admin_ui')) &&
                        <Box>{defaultTexts.openStackAdminUi}</Box>
                      }
                      
                      {
                        (configParams && configParams.visible_to_service && configParams.visible_to_service.includes('custom_client_ui')) &&
                        <Box>{defaultTexts.openStackCustomerUi}</Box>
                      }
                    </Box>
                  </Stack>
                }
              </>                     
            }
          </> :
          <>{ isIdentityProvider ? defaultTexts.onlyOneIdentityProviderText : defaultTexts.notConfigured }</>
        }
      </CustomCard>
    </Box>
  )
}

export default RegionServiceItem